import { Link } from 'react-router';
import { archiveApi, type ArchiveHealthLine, type ArchiveHealthState } from '../../api/client';
import { Button, Card, Chip, LoadError, Loading } from '../../components/ui';
import type { Tone } from '../../lib/format';
import { shortWhen } from '../../lib/format';
import { useData } from '../../lib/useData';

/**
 * Stitch Admin → System → Archive health (1.13.0, Teampace 3123, proposal 112; help: staff-archive):
 * how the archive is doing, platform-wide, each line with a plain state and what to do about it. Is
 * stitch-archive answering and running its passes, is the outbox draining (the oldest waiting item,
 * what's failing), when each kind was last archived, compaction, companies' own copies, retention
 * runs and the bucket's test. stitch-monitor alerts on the same lines. It changes nothing; per company
 * is Stitch Admin → Archive's.
 */

const TONE: Record<ArchiveHealthState, Tone> = { ok: 'ok', warn: 'warn', problem: 'crit', off: 'neutral' };
const WORD: Record<ArchiveHealthState, string> = { ok: 'OK', warn: 'Look', problem: 'Problem', off: 'Not in use' };

export function ArchiveHealthCard() {
  const data = useData(() => archiveApi.health(), [], { live: false });
  const h = data.data;
  return (
    <Card
      title="Archive health"
      action={
        h ? (
          <span className="deploy-day-head">
            <Chip tone={TONE[h.worst]}>{h.worst === 'ok' ? 'All well' : h.worst === 'problem' ? 'Needs fixing' : h.worst === 'warn' ? 'Worth a look' : 'Not in use'}</Chip>
            <Button size="small" icon="refresh" onClick={data.reload}>
              Check again
            </Button>
          </span>
        ) : undefined
      }
    >
      {data.error ? (
        <LoadError error={data.error} retry={data.reload} />
      ) : !h ? (
        <Loading label="Checking the archive" />
      ) : (
        <>
          <div className="card-body" style={{ paddingBottom: 0 }}>
            <span className="muted">
              Every company together, checked {shortWhen(h.checkedAt)}. stitch-monitor alerts on the same lines. For one company, see <Link to="/admin/archive">Archive by company</Link>.
            </span>
          </div>
          <ul className="plain-list blocker-list deploy-day archive-health" aria-label="Archive health">
            {h.lines.map((l) => (
              <li key={l.id} className="blocker-row" data-state={l.state}>
                <span className="cell-stack">
                  <span>
                    <Title line={l} />
                  </span>
                  <small>{l.said}</small>
                  {l.todo && <small className="muted">{l.todo}</small>}
                </span>
                <Chip tone={TONE[l.state]} small>
                  {WORD[l.state]}
                </Chip>
              </li>
            ))}
          </ul>
          {h.kinds.length > 0 && (
            <div className="table-wrap">
              <table className="table compact" aria-label="Archived by kind">
                <thead>
                  <tr>
                    <th>Kind</th>
                    <th>Last archived</th>
                    <th>Waiting</th>
                    <th>Failing</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {h.kinds.map((k) => (
                    <tr key={k.kind} title={k.lastError ?? undefined}>
                      <td>{k.label}</td>
                      <td>{k.lastDoneAt ? shortWhen(k.lastDoneAt) : 'Nothing yet'}</td>
                      <td>
                        {k.waiting.toLocaleString('en-US')}
                        {k.waiting && k.oldestQueuedAt ? <small className="muted"> since {shortWhen(k.oldestQueuedAt)}</small> : null}
                      </td>
                      <td>
                        {(k.failing + k.parked).toLocaleString('en-US')}
                        {k.parked ? <small className="muted"> ({k.parked.toLocaleString('en-US')} stopped)</small> : null}
                      </td>
                      <td>
                        <Chip tone={TONE[k.state]} small>
                          {WORD[k.state]}
                        </Chip>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

/** The line's title, linking to where it's looked into: a card on System by its anchor, or another page. */
function Title({ line }: { line: ArchiveHealthLine }) {
  const title = <strong>{line.title}</strong>;
  if (!line.to) return title;
  const hash = line.to.startsWith('/admin/system#') ? line.to.slice('/admin/system'.length) : null;
  return hash ? <a href={hash}>{title}</a> : <Link to={line.to}>{title}</Link>;
}
